import {
  AlarmClock,
  BarChart3,
  Bot,
  Calculator,
  CalendarDays,
  Camera,
  Check,
  CheckCircle2,
  ChevronRight,
  Crown,
  Edit3,
  Gift,
  Info,
  Link as LinkIcon,
  Medal,
  MonitorUp,
  Play,
  Shield,
  Sparkles,
  Target,
  Trophy,
  UsersRound,
  Wallet,
  X,
} from "lucide-react";
import {
  FiAlertTriangle,
  FiTarget,
  FiMonitor,
  FiUsers,
} from "react-icons/fi";
import { FaRobot, FaCamera } from "react-icons/fa";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { useUser } from "../../../../hooks/use-user";
import axios from "../../../../axios";
import { getAppControls } from "../../../../services/configService";
import ContestResultModal from "./contestResultModal";
import { getContestReturnUrl } from "./contestNavigation";

const parseNumberPrizePool = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString("en-IN")
    : fallback.toLocaleString("en-IN");
};

const parseNumber = (value, fallback = 0) => { const number = Number(value); return Number.isFinite(number) ? number : fallback; };

const formatRupeesFromCredits = (credits) => {
  const rupees = parseNumber(credits, 0) / 100;
  return Number.isInteger(rupees) ? rupees.toString() : rupees.toFixed(2);
};

const parseBoolean = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value).toLowerCase() === "true";

const formatHHMMSS = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value) => value.toString().padStart(2, "0");

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const getTimeMs = (value) => {
  if (!value) return null;
  const timeMs = new Date(value).getTime();
  return Number.isFinite(timeMs) ? timeMs : null;
};

const getEntryDeadlineMs = (item) =>
  getTimeMs(item?.entryTime) ?? getTimeMs(item?.startTime);

const getContestSlotMeta = (item) => {
  const baseTotalSlots = Math.max(
    1,
    parseNumber(item?.totalSlot ?? item?.totalSlots ?? item?.maxSlot, 200),
  );
  const addOnSlots = parseBoolean(item?.showAddOnSlots)
    ? Math.max(0, parseNumber(item?.addOnSlots, 0))
    : 0;
  const totalSlots = baseTotalSlots;
  const filledSlots = Math.max(
    0,
    parseNumber(
      item?.joinedUsersCount ??
        item?.joinCount ??
        item?.joinedCount ??
        item?.filledSlots,
    ) + addOnSlots,
  );

  return {
    totalSlots,
    filledSlots,
    slotsLeft: Math.max(0, totalSlots - filledSlots),
  };
};

const getResultDeclarationValue = (item) =>
  item?.resultDeclarationTime ??
  item?.resultDeclaredOn ??
  item?.resultDeclaredAt ??
  item?.resultDeclareTime ??
  item?.resultDeclareOn ??
  item?.resultTime ??
  item?.resultOn ??
  item?.resultDate ??
  item?.declarationTime ??
  item?.declarationDate ??
  item?.endTime ??
  null;

const formatResultDeclarationLabel = (value) => {
  if (typeof value !== "string") return "Result pending";

  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === "invalid date") {
    return "Result pending";
  }

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return trimmed;

  return date.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getCountdownText = (targetMs, now) => {
  if (targetMs === null || now >= targetMs) return "00:00:00";
  return formatHHMMSS(targetMs - now);
};

const getTimerData = (startTime, endTime, now) => {
  const startMs = getTimeMs(startTime);
  const endMs = getTimeMs(endTime);

  if (startMs !== null && now < startMs) {
    return { phase: "starts", text: formatHHMMSS(startMs - now) };
  }

  if (endMs !== null && now < endMs) {
    return { phase: "ends", text: formatHHMMSS(endMs - now) };
  }

  return { phase: "expired", text: "00:00:00" };
};

const isContestRunningAt = (item, now) => {
  const startMs = getTimeMs(item?.startTime);
  const endMs = getTimeMs(item?.endTime);
  return startMs !== null && endMs !== null && now >= startMs && now < endMs;
};

const isSameContest = (joinedContest, contestId) => {
  if (!contestId) return false;

  if (typeof joinedContest === "string") {
    return String(joinedContest) === String(contestId);
  }

  return [joinedContest?._id, joinedContest?.contestId, joinedContest?.id].some(
    (id) => id && String(id) === String(contestId),
  );
};

const isUserJoinedContest = (item, user, localJoined = false) => {
  if (localJoined) return true;

  if (
    item?.isJoined === true ||
    item?.joined === true ||
    item?.isContestJoined === true
  ) {
    return true;
  }

  const contestId = item?._id || item?.contestId || item?.id;
  const joinedContests = user?.joinedContests;

  return Array.isArray(joinedContests)
    ? joinedContests.some((joinedContest) =>
        isSameContest(joinedContest, contestId),
      )
    : false;
};

const isContestCancelled = (item) =>
  parseBoolean(item?.isCancelled) ||
  parseBoolean(item?.isCancell) ||
  parseBoolean(item?.isCancel);

const getButtonState = (item, user, now, localJoined = false) => {
  if (isContestCancelled(item)) {
    return { state: "cancelled", label: "Contest Cancelled" };
  }

  const joined = isUserJoinedContest(item, user, localJoined);
  const entryDeadlineMs = getEntryDeadlineMs(item);
  const endMs = getTimeMs(item?.endTime);
  const expired = endMs !== null ? now >= endMs : false;
  const contestRunning = isContestRunningAt(item, now);
  const entryOpen = entryDeadlineMs !== null ? now < entryDeadlineMs : false;
  const { slotsLeft } = getContestSlotMeta(item);
  const slotsFull = slotsLeft <= 0;

  const isPremium = item?.isCourseRequired === true;
  const hasPlan = user?.planName !== "Test";
  const isContestSubmitted =
    item?.isContestSubmitted === true || item?.isSubmitted === true;

  if (expired) {
    if (joined) return { state: "result", label: "Result" };
    return { state: "closed", label: "Contest Expired" };
  }

  if (contestRunning) {
    if (isContestSubmitted) {
      return { state: "summary", label: "Summary" };
    }
    if (joined) return { state: "play_now", label: "Play" };
  }

  if (joined) return { state: "reminder", label: "Reminder Me" };
  if (slotsFull) return { state: "slots_full", label: "Slots Full" };
  if (!entryOpen) return { state: "closed", label: "Entry Closed" };
  if (isPremium && !hasPlan) {
    return { state: "unlock_course", label: "UNLOCK COURSE" };
  }

  return { state: "join_now", label: "Join" };
};

const CONTEST_INFO_POINTS = [
  {
    icon: <LinkIcon className="shrink-0 text-[#E0A72F]" />,
    text: "This is a skill-based contest. Rewards are based on performance and are not guaranteed. Winners are determined purely based on performance.",
  },
  {
    icon: <Calculator className="shrink-0 text-[#E0A72F]" />,
    text: "Score = Right - (Wrong x 0.25)",
  },
  {
    icon: <BarChart3 className="shrink-0 text-[#E0A72F]" />,
    text: "Rankings are calculated automatically by the system.",
  },
  {
    icon: (
      <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#F4A322] text-[9px] font-bold text-[#2B2538]">
        L2E
      </span>
    ),
    text: "Rewards are credited after result declaration.",
  },
  {
    icon: <Shield className="shrink-0 text-[#E0A72F]" />,
    text: "Only one attempt allowed.",
  },
  {
    icon: <AlarmClock className="shrink-0 text-[#E0A72F]" />,
    text: "Auto submit when timer ends.",
  },
  {
    icon: <Edit3 className="shrink-0 text-[#E0A72F]" />,
    text: "Rankings cannot be manually modified.",
  },
];

const RANKING_PRIORITY_POINTS = [
  "Highest Score",
  "Higher Accuracy",
  "Less Time Taken",
  "Earlier Submission",
];

const ContestCard = ({ contest, onJoin, onWalletUpdated }) => {
  const { data: user } = useUser();
  const navigate = useNavigate();
  const toast = useToast({ position: "top" });
  const [showInfo, setShowInfo] = useState(false);
  const [joinPopupOpen, setJoinPopupOpen] = useState(false);
  const [joinStep, setJoinStep] = useState("confirm");
  const [passKey, setPassKey] = useState("");
  const [localJoined, setLocalJoined] = useState(false);
  const [joining, setJoining] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [summaryResult, setSummaryResult] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showWinners, setShowWinners] = useState(false);
  const [showReminderPopup, setShowReminderPopup] = useState(false);
  const [showFairPlayPopup, setShowFairPlayPopup] = useState(false);
  const [showRupees, setShowRupees] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState("");

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchAppControls = async () => {
      try {
        const response = await getAppControls();
        setShowRupees(parseBoolean(response?.result?.showRupees));
      } catch (error) {
        console.log("Failed to fetch app controls:", error);
      }
    };

    fetchAppControls();
  }, []);

  const nowMs = currentTime;
  const timer = getTimerData(contest?.startTime, contest?.endTime, nowMs);
  const entryDeadlineMs = getEntryDeadlineMs(contest);
  const entryCountdown = getCountdownText(entryDeadlineMs, nowMs);
  const buttonState = getButtonState(contest, user, nowMs, localJoined);
  const isPremium = contest?.isCourseRequired === true;
  const { totalSlots, filledSlots, slotsLeft } = getContestSlotMeta(contest);
  const progress = Math.min(100, (filledSlots / totalSlots) * 100);
  const winners = Math.max(0, parseNumber(contest?.winners, 0));
  const prizePoolCoin = parseNumberPrizePool(contest?.prizePoolCoin, 0);
  const entryFee = parseNumber(contest?.entryCoin, 0);
  const entryFeeLabel = showRupees
    ? `Credits ${entryFee} (₹ ${formatRupeesFromCredits(entryFee)})`
    : `Credits ${entryFee}`;
  const duration = parseNumber(contest?.duration, 45);
  const resultDeclarationTime = getResultDeclarationValue(contest);
  const resultDeclarationLabel = formatResultDeclarationLabel(
    resultDeclarationTime,
  );
  const startTime = contest?.startTime;
  const isPassKeyRequired = parseBoolean(contest?.isPassKeyRequired);
  

  const formatTime = (isoDate) => {
    if (!isoDate) return "--";

    const date = new Date(isoDate);

    return date.toLocaleString("en-IN", {
      // day: "2-digit",
      // month: "short",
      // year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDateTime = (isoDate) => {
    if (!isoDate) return "--";

    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return "--";

    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

const getStartLabel = (startTime) => {
  if (!startTime) return "";

  const date = new Date(startTime);

  const month = date.toLocaleString("en-IN", { month: "short" });
  const day = date.getDate();

  const time = date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${month} ${day} ${time}`;
};
  const startLabel = getStartLabel(contest.startTime, contest.contestEnded);
  const reminderStartLabel = formatDateTime(contest?.startTime);
  const reminderEndLabel = formatDateTime(contest?.endTime);
  const durationDisplay = parseBoolean(contest?.forNewUserOnly)
    ? `${contest?.timing} min` || `${duration} min`
    : `${duration} min`;
  const entryCloseLabel = formatTime(
  new Date(new Date(contest.endTime).getTime())
);
  // Example
  const walletCoin =
    (user?.unifiedWallet?.rechargedBalance || 0) +
    (user?.unifiedWallet?.contestBalance || 0) +
    (user?.unifiedWallet?.referralBalance || 0) +
    (user?.unifiedWallet?.lockedBalance || 0) +
    (user?.coinWallet || 0) +
    (user?.walletBalance || 0) +
    (user?.balance || 0);

  const hasInsufficientBalance = walletCoin < entryFee;

  const isCardGreyOut =
    buttonState.state === "cancelled" ||
    buttonState.state === "closed" ||
    buttonState.state === "result" ||
    buttonState.state === "slots_full";
  const badgeLabel = isPremium
    ? "Premium"
    : buttonState.state === "cancelled"
      ? "Cancelled"
    : buttonState.state === "summary"
      ? "Summary"
      : buttonState.state === "result"
        ? "Finish"
        : timer.phase === "starts"
          ? "Upcoming"
          : timer.phase === "ends"
            ? "Live"
            : "Expired";
  const badgeClass = isPremium
    ? "bg-[#442A68] text-white"
    : buttonState.state === "cancelled"
      ? "bg-[#F06565] text-white"
    : buttonState.state === "summary"
      ? "bg-[#3B82F6] text-white"
      : buttonState.state === "result"
        ? "bg-[#60A5FA] text-white"
        : timer.phase === "starts"
          ? "bg-[#F8B028] text-[#6b4900]"
          : timer.phase === "ends"
            ? "bg-[#4ADE80] text-white"
            : "bg-[#F06565] text-white";
  const cardBorderClass =
    buttonState.state === "play_now"
      ? "border-[#F7A51E]"
      : buttonState.state === "summary"
        ? "border-[#BFDBFE]"
        : buttonState.state === "closed" || buttonState.state === "cancelled"
          ? "border-[#D1D5DB]"
          : "border-[#E5DDF3]";
  const slotTextClass =
    buttonState.state === "slots_full"
      ? "text-[#FF5A5F]"
      : isCardGreyOut
        ? "text-[#A3A3A3]"
        : "text-[#756d81]";
  const slotFillClass =
    buttonState.state === "slots_full"
      ? "bg-[#FF5A5F]"
      : isCardGreyOut
        ? "bg-[#A3A3A3]"
        : "bg-[#F7A51E]";
  const prizeRows = useMemo(() => {
    if (!Array.isArray(contest?.winnerPrizes)) return [];
    return contest.winnerPrizes
      .map((coins, index) => ({
        rank: `Rank ${index + 1}`,
        coins: parseNumber(coins, 0),
      }))
      .filter((row) => row.coins > 0);
  }, [contest?.winnerPrizes]);

  const popupRules = useMemo(() => {
    const sourceRules = contest?.rules ?? contest?.contestRules;
    if (Array.isArray(sourceRules)) {
      const resolved = sourceRules
        .filter((value) => typeof value === "string")
        .map((value) => value.trim())
        .filter(Boolean);
      return resolved.length ? resolved : null;
    }
    return null;
  }, [contest?.contestRules, contest?.rules]);

  const normalizeSummaryResult = (details = {}, questionCount = 0) => {
    const rightCount = details.totalRightAnswerCount || details.rightCount || 0;
    const wrongCount = details.totalWrongAnswerCount || details.wrongCount || 0;
    const totalAttempts =
      details.totalAttempts ||
      Number(rightCount || 0) + Number(wrongCount || 0);
    const totalQuestions =
      questionCount ||
      details.totalQuestions ||
      details.totalQuestion ||
      details.totalContestQuestions ||
      totalAttempts;
    const skipCount =
      details.totalSkipCount ??
      details.skipCount ??
      Math.max(Number(totalQuestions || 0) - Number(totalAttempts || 0), 0);
    const score = Math.max(
      Number(rightCount || 0) - Number(wrongCount || 0) * 0.25,
      0,
    );

    return {
      ...details,
      totalQuestions,
      rightCount,
      wrongCount,
      skipCount,
      totalAttempts,
      score,
      finalScore: score,
      accuracy: details.accuracy || 0,
      userName: details.userName || details.name || user?.name,
      name: details.name || details.userName || user?.name,
      contestId: details.contestId || contest._id,
      contestName: details.contestName || contest.contestName,
      totalContestSlot: details.totalContestSlot,
      userRank: details.hasContestEnded ? details.userRank : null,
      winningAmount: details.hasContestEnded ? details.winningAmount : 0,
      totalUserContestJoinedCount: details.totalUserContestJoinedCount,
      earnedCoins: details.hasContestEnded ? details.winningAmount || 0 : 0,
      resultDeclareTime:
        details.resultDeclareTime ||
        details.resultDeclaredAt ||
        getResultDeclarationValue(details) ||
        getResultDeclarationValue(contest),
      submittedAt:
        details.submittedAt || details.updatedAt || details.createdAt,
    };
  };

  const openJoinPopup = () => {
    setErrorMessage("");
    setPassKey("");
    setJoinStep("confirm");
    setJoinPopupOpen(true);
  };
  const openPremiumPopup = () => {
  
    setPopupType("premium");
    setShowPopup(true);
  };
  
  const handleConfirmJoin = async () => {
    if (isPassKeyRequired && !passKey.trim()) {
      setErrorMessage("Please enter pass key");
      return;
    }

    setJoining(true);
    setErrorMessage("");

    try {
      const result = await onJoin(
        contest._id,
        entryFee,
        contest.contestName,
        passKey.trim(),
      );

      if (result?.success) {
        setLocalJoined(true);
        if (isContestRunningAt(contest, Date.now())) {
          setJoinStep("joined");
        } else {
          setJoinPopupOpen(false);
          setShowReminderPopup(true);
        }
        return;
      }

      if (result?.insufficient) {
        setJoinPopupOpen(false);
        return;
      }

      setErrorMessage(result?.message || "Unable to join contest");
    } catch {
      setErrorMessage("Unable to join contest");
    } finally {
      setJoining(false);
    }
  };

  const handleViewSummary = async () => {
    if (summaryLoading) return;

    setSummaryLoading(true);
    setShowSummaryModal(true);
    setSummaryResult(null);

    try {
      const [detailsRes, questionsRes] = await Promise.all([
        axios.get("contest-result-summary", {
          params: { contestId: contest._id },
        }),
        axios.get(`getContestQuestionsByContestId/${contest._id}`),
      ]);

      const details = detailsRes?.data?.result || {};
      const questions = questionsRes?.data?.result?.questions;
      const questionCount = Array.isArray(questions) ? questions.length : 0;

      setSummaryResult(normalizeSummaryResult(details, questionCount));
    } catch (error) {
      console.log(error);
      setSummaryResult(normalizeSummaryResult(contest));
    } finally {
      setSummaryLoading(false);
    }
  };

  const showNoQuestionsToast = () => {
    toast({
      duration: 3500,
      isClosable: true,
      render: ({ onClose }) => (
        <div className="mx-3 rounded-2xl border border-[#E0A72F] bg-[#FFF3DD] px-4 py-4 text-[#B76812] shadow-lg">
          <div className="flex items-center justify-between gap-4">
            <p className="text-base font-medium leading-6">
              You have attempted all the questions in this contest, please wait
              for the next contest
            </p>
            <button
              type="button"
              onClick={onClose}
              className="border-l border-[#D08B1E] pl-3 text-3xl leading-none"
            >
              x
            </button>
          </div>
        </div>
      ),
    });
  };

  const hasContestQuestions = async () => {
    try {
      const response = await axios.get(
        `getContestQuestionsByContestId/${contest._id}`,
      );
      const questions = response?.data?.result?.questions;
      return Array.isArray(questions) && questions.length > 0;
    } catch {
      return false;
    }
  };

  const proceedToContestQuestion = async () => {
    if (playing || buttonState.state !== "play_now") return;
    setPlaying(true);

    const hasQuestions = await hasContestQuestions();
    if (!hasQuestions) {
      showNoQuestionsToast();
      setPlaying(false);
      setShowFairPlayPopup(false);
      return;
    }

    setJoinPopupOpen(false);
    setShowFairPlayPopup(false);
    setShowCelebration(true);
    setTimeout(() => {
      const returnUrl = getContestReturnUrl();
      if (isContestRunningAt(contest, nowMs)) {
        navigate("/dash/contest-question", {
          state: { contestId: contest._id, returnUrl },
        });
      } else {
        navigate(returnUrl, {
          state: { contestId: contest._id },
        });
      }
    }, 800);
  };

  const handlePlayContest = () => {
    if (playing || buttonState.state !== "play_now") return;
    setShowFairPlayPopup(true);
  };

  const handleActionPress = async () => {
    if (buttonState.state === "join_now") {
      openJoinPopup();
      return;
    }

    if (buttonState.state === "slots_full") {
      toast({
        title: "All contest slots are full.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (buttonState.state === "unlock_course") {
      openPremiumPopup();
      // navigate("/dash/plan")
      return;
    }

    if (buttonState.state === "reminder") {
      setShowReminderPopup(true);
      return;
    }

    if (buttonState.state === "play_now") {
      handlePlayContest();
      return;
    }

    if (buttonState.state === "summary") {
      await handleViewSummary();
      return;
    }

    if (buttonState.state === "result") {
      await onWalletUpdated?.();
      navigate("/dash/contest-winner", {
        state: {
          contestId: contest._id,
          showAnswerKey: contest.showAnswerKey,
          returnUrl: getContestReturnUrl(),
        },
      });
    }
  };

      const isContestActive =
  buttonState.state !== "closed";
  const renderActionButton = () => {
    const actionButtonClass =
      "min-w-[105px] rounded-xl px-4 py-2 text-center text-[15px] font-bold leading-tight transition disabled:opacity-70";

    if (
      buttonState.state === "cancelled" ||
      buttonState.state === "closed" ||
      buttonState.state === "joined"
    ) {
      return (
        <button
          type="button"
          disabled
          className={`${actionButtonClass} ${
            buttonState.state === "joined"
              ? "bg-[#3F234E] text-white"
              : "border border-[#D1D5DB] bg-[#F3F4F6] text-[#A3A3A3]"
          }`}
        >
          {buttonState.label}
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={handleActionPress}
        disabled={playing || summaryLoading}
        className={`${actionButtonClass} ${
          buttonState.state === "slots_full"
            ? "border border-[#D1D5DB] bg-[#F3F4F6] text-[#A3A3A3]"
            : buttonState.state === "unlock_course"
              ? "relative bg-[#3F234E] text-white"
              : buttonState.state === "summary"
                ? "bg-[#562566] text-white"
                : buttonState.state === "result"
                  ? "bg-[#3F234E] text-white"
                  : "bg-[#3F234E] text-white"
        }`}
      >
        {buttonState.state === "play_now" ? (
          <>
            Play
            <span className="mt-1 block text-xs font-medium">Go now</span>
          </>
        ) : entryFee === 0 && buttonState.state === "join_now" ? (
          <>
            <span className="text-[#F8B028]">Free</span>
            <span className="mt-1 block text-xs font-medium text-white">
              Free Entry
            </span>
          </>
        ) : buttonState.state === "join_now" ? (
          <>
            Join
            <span className="mt-1 block text-xs font-medium text-[#F8B028]">
              {entryFeeLabel}
            </span>
          </>
        ) : buttonState.state === "unlock_course" ? (
          <span className="relative inline-flex items-center gap-2">
            <Crown
              className="absolute -left-5 -top-2 h-5 w-5 rotate-[-18deg] text-[#F5A623]"
              fill="#F5A623"
            />
            <span>{buttonState.label}</span>
          </span>
        ) : buttonState.state === "summary" ||
          buttonState.state === "result" ? (
          <span className="inline-flex items-center gap-1">
            <span>{summaryLoading ? "Loading..." : buttonState.label}</span>
            <ChevronRight size={16} />
          </span>
        ) : (
          buttonState.label
        )}
      </button>
    );
  };

  return (
    <>
      <div className="relative">
        <div
          className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br from-[#f6f4fa] to-[#ece9f3] shadow-[0_10px_30px_rgba(75,45,131,0.15)] ${cardBorderClass}`}
        >
          <div className="absolute -bottom-10 -left-14 h-40 w-40 rounded-full bg-[#e7dff5] opacity-40" />
          {/* <div className="absolute inset-0 -z-10 bg-[linear-gradient(135deg,rgba(255,255,255,0.96),rgba(246,240,252,0.88),rgba(255,255,255,0.9))]" />

          <div className="absolute -top-8 left-0 right-14 -z-10 h-32  rounded-b-[60px] bg-[linear-gradient(135deg,rgba(255,255,255,0.72),rgba(255,255,255,0.18),rgba(255,255,255,0))]" /> */}

          <div className="relative p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {/* {buttonState.state !== "closed" &&
                  buttonState.state !== "result" &&
                  buttonState.state !== "summary" && (
                    <div className="absolute left-1/2 -top-8 z-50 mt-2 -translate-x-1/2">
                      <div className="min-w-[130px] rounded-full bg-[#34163F] px-10 py-1 text-center text-[10px] font-bold text-white shadow-lg">
                        <span className="text-white">
                          {buttonState.state === "join_now"
                            ? "Entry closes in"
                            : "Starts in"}
                        </span>
                        <span className="text-[#F8B028]">
                          {buttonState.state === "join_now"
                            ? entryCountdown
                            : timer.text}
                        </span>
                      </div>
                    </div>
                  )} */}
                {/* {buttonState.state !== "closed" && buttonState.state !== "result" && buttonState.state !== "summary" && (
  <div className="absolute left-1/2 -top-5 z-50 mt-2 -translate-x-1/2">
    <div className="min-w-[130px] rounded-b-2xl bg-[#34163F] px-5 py-2 text-center text-sm font-bold text-white shadow-lg">
      {startLabel}
    </div>
  </div>
)} */}
                <div className="flex mt-0 items-start justify-between gap-3">
                  <h3 className="text-md text-left font-bold leading-none text-[#2d2438]">
                    {contest.contestName || "Contest Name"}
                  </h3>

                  <span
                    className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${badgeClass}`}
                  >
                    {badgeLabel}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => setShowInfo(true)}
                    className="flex items-center gap-2 text-sm text-[#5e556a]"
                  >
                    View Contest Rules
                    <Info size={16} className="text-[#34163F]" />
                  </button>

                  <p className="text-right text-sm font-semibold text-[#3b3445]">
                    Duration : {durationDisplay}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-2">
              <div className="flex flex-1 items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF3DD]">
                  <Trophy className="text-[#F2A51A]" size={25} />
                </div>
                <div>
                  <p className="text-left text-md font-bold leading-none text-[#2d2438]">
                    {prizePoolCoin}
                  </p>
                  <p className="text-[10px] text-[#756d81]">
                    Total credits Rewards
                  </p>
                </div>
              </div>

              {renderActionButton()}
            </div>

          <div
  className={`m-2 flex items-center justify-between text-[15px] ${
    isContestActive ? "text-red-500" : slotTextClass
  }`}
>
  {isPremium ? (
    <>
      <span>
        <span
          className={
            isContestActive
              ? "text-red-500"
              : isCardGreyOut
              ? "text-[#A3A3A3]"
              : "text-[#FF3A3A]"
          }
        >
          {filledSlots}/
        </span>
        {totalSlots}{" "}
        <span
          className={
            isContestActive
              ? "text-red-500"
              : isCardGreyOut
              ? "text-[#A3A3A3]"
              : "text-[#F8B028]"
          }
        >
          Elite Slots
        </span>
      </span>

      <span className={isContestActive ? "text-red-500" : ""}>
        Limited Access
      </span>
    </>
  ) : (
    <>
      <span className={isContestActive ? "text-red-500" : ""}>
        {filledSlots}/{totalSlots} slots
      </span>

      <span className={isContestActive ? "text-red-500" : ""}>
        {slotsLeft === 0 ? "Slots Full" : `${slotsLeft} Spots Left`}
      </span>
    </>
  )}
</div>

            <div className="h-1 rounded-full bg-[#d5cfdd]">
              <div
                className={`h-full rounded-full ${slotFillClass}`}
                style={{ width: `${progress}%` }}
              />
            </div>
    <div className="rounded-2xl p-0">

  {/* Bottom Row */}
<div className="flex items-start justify-between rounded-xl border border-gray-200 bg-white/30 shadow-xl p-4 mt-2">
  {/* Left Side */}
  <div>
    <p className="text-sm font-medium text-[#F4A300]">
      1st Prize
    </p>

    <p className="mt-1 text-md font-bold text-[#34163F]">
      Credits{" "}
      {prizeRows?.[0]?.coins?.toLocaleString("en-IN") || 0}
    </p>
  </div>

  {/* Right Side */}
  <div className="text-right">
    <p className="text-sm font-semibold text-gray-500">
      Starts
    </p>

    <p className="mt-1 text-base font-semibold text-[#34163F]">
      {getStartLabel(contest?.startTime, contest?.contestEnded)}
    </p>
  </div>
</div>
</div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (
                !isPremium &&
                buttonState.state !== "closed" &&
                buttonState.state !== "slots_full" &&
                prizeRows.length
              ) {
                setShowWinners((prev) => !prev);
              }
            }}
            className={`relative z-10 flex min-h-[42px] w-full items-center justify-between gap-3 px-3 py-2 text-sm font-bold rounded-b-[20px] ${
              isCardGreyOut
                ? "bg-gray-300 text-gray-500"
                : "bg-[#3F234E] text-white"
            }`}
          >
            {buttonState.state === "closed" ? (
              <div className="flex w-full justify-end">{buttonState.label}</div>
            ) : buttonState.state === "slots_full" ? (
              <div className="flex w-full justify-end text-[#FF5A5F]">
                Contest Full
              </div>
            ) : buttonState.state === "result" ? (
              <div className="flex w-full justify-end text-[#F8B028]">
                Gate Result
              </div>
            ) : buttonState.state === "summary" ? (
              <>
                <div className="flex items-center gap-2 text-white">
                  <AlarmClock size={16} />
                  <span>Summary available</span>
                </div>
                <div className="text-right text-[11px] leading-tight text-white/85">
                  <span className="block text-[#D7C6F2]">Result Declare</span>
                  <span className="font-bold text-white">
                    {resultDeclarationLabel}
                  </span>
                </div>
              </>
            ) : isPremium ? (
              <>
                <div className="flex items-center gap-2 text-[#F8B028]">
                  <AlarmClock size={16} />
                  <span>Ends in {timer.text}</span>
                </div>
                <div className="text-[#F8B028]">Elite Only</div>
              </>
            ) : buttonState.state === "play_now" ? (
              <>
                <div className="flex items-center gap-2 text-white">
                  <AlarmClock size={16} />
                  <span>Ends in {timer.text}</span>
                </div>
                <div>Hurry Up!</div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-white text-[12px]">
                  <Trophy size={16} />
                  <span className="font-semibold">view {winners} Ranking</span>
                  {prizeRows.length > 0 && (
                    <span
                      className={`transition-transform duration-200 ${showWinners ? "rotate-180" : "rotate-0"}`}
                    >
                      ▲
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-right text-[13px]">
                  <div className="min-w-[130px] rounded-b-2xl  text-center text-[12px] text-white">
                    <span className="text-white">
                          {buttonState.state === "join_now"
                            ? "Entry closes :"
                            : "Starts in"}
                        </span>
                        <span>-</span>
                        <span className="text-[#F8B028]">
                          {buttonState.state === "join_now"
                            ? entryCountdown
                            : timer.text} Hrs
                        </span>
                  </div>
                </div>
              </>
            )}
          </button>

          {showWinners && prizeRows.length > 0 && (
             <div className="mt-3 max-h-[120px] overflow-y-auto  hide-scrollbar">
              <div className="space-y-2">
                {prizeRows.map((row, index) => {
                  const highlight = index < 3;
                  return (
                    <div
                      key={`${row.rank}-${index}`}
                      className={`flex items-center rounded-xl px-3 py-2 ${highlight ? "bg-[#FF7F50] text-white" : "bg-[#E5DDF3] text-[#2F2645]"}`}
                    >
                      <Trophy
                        size={14}
                        className={highlight ? "text-white" : "text-[#562566]"}
                      />
                      <span className="ml-2 flex-1 text-sm font-semibold">
                        {row.rank}
                      </span>
                      <span className="text-sm font-bold">
                        Credits {row.coins}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {joinPopupOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/35 px-6 lg:pt-12 backdrop-blur-md"
          onClick={() => !joining && setJoinPopupOpen(false)}
        >
          <div
            className="relative w-full max-w-[600px] overflow-y-auto rounded-[30px] border-4 border-white/70 bg-white px-4 pb-2 pt-6 text-[#2E2933] shadow-[0_20px_60px_rgba(37,21,47,0.35)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => !joining && setJoinPopupOpen(false)}
              className="absolute right-2 top-0 flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#1f1b24] shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
            >
              <X size={22} />
            </button>

            <div className="mx-auto mb-2 rounded-[15px] bg-[#562566] px-4 py-2 text-white shadow-sm">
              <div className="flex items-center justify-center gap-2 text-[12px] font-semibold leading-none">
                <Trophy size={18} className="text-[#F2A51A]" />
                <span>Total Rewards</span>
                <span className="font-bold text-[#FFD052]">
                  {contest.prizePoolCoin} Credits
                </span>
              </div>
            </div>

            <h2 className="text-center text-[20px] font-bold leading-tight text-[#242026]">
              {contest.contestName}
            </h2>

            <div className="mx-auto mt-2 w-fit rounded-lg bg-[#F2A51A] px-6 py-2 text-[16px] font-bold text-white shadow-sm">
              Unlock Contest Using {contest.entryCoin || 0} Credits
            </div>

            <div className="mt-2 rounded-[14px] border-2 border-[#E4B94A] bg-white px-4 py-2">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-[14px] font-sm text-[#2C272E]">
                  <Wallet size={21} className="text-[#2C272E]" />
                  Available Credits
                </span>
                <span className="text-[18px] font-bold text-[#211A28]">
                  {walletCoin}
                </span>
              </div>
            </div>

            <div className="mt-2 rounded-[18px] bg-white px-4 py-2 shadow-[0_14px_38px_rgba(40,22,56,0.08)]">
              <h3 className="mb-2 text-center text-[21px] font-bold text-[#CC8500]">
                Rules Card
              </h3>

              <div className="space-y-2 text-[12px] text-[#5F5968]">
                {(() => {
                  const minSolve = Math.max(
                    0,
                    parseInt(contest?.minimumQuestionsToSolve, 10) || 0,
                  );
                  const baseRules = [
                    {
                      icon: <Sparkles size={12} />,
                      text: "Skill-based contest (not luck based)",
                    },
                  {
                    icon: <Trophy size={12} />,
                    text: "Score = Right - (Wrong x 0.25)",
                  },
                  {
                    icon: <BarChart3 size={12} />,
                    text: "Rank depends on Score - Accuracy - Time",
                  },
                  {
                    icon: <Shield size={12} />,
                    text: "Only one attempt allowed",
                  },
                  {
                    icon: <AlarmClock size={12} />,
                    text: "Auto submit when timer ends",
                  },
                  {
                    icon: <Sparkles size={12} />,
                    text: "Rewards are based on performance and are not guaranteed",
                  },
                  ];
                  if (minSolve > 0) {
                    baseRules.splice(2, 0, {
                      icon: <Target size={12} />,
                      text: `Solve at least ${minSolve} question${minSolve === 1 ? "" : "s"} correctly to be eligible for rewards`,
                    });
                  }
                  return baseRules;
                })().map((rule) => (
                  <div key={rule.text} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-[#D9A12F]">
                      {rule.icon}
                    </span>
                    <p className="text-left">{rule.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {errorMessage && (
              <p className="mt-4 text-center text-sm font-semibold text-red-500">
                {errorMessage}
              </p>
            )}

            <div className="mt-2 space-y-2">
              {joinStep === "confirm" ? (
                <>
                  {isPassKeyRequired && (
                    <div>
                      <label
                        htmlFor={`contest-pass-key-${contest._id}`}
                        className="mb-1 block text-sm font-semibold text-[#2C272E]"
                      >
                        Pass Key
                      </label>
                      <input
                        id={`contest-pass-key-${contest._id}`}
                        type="text"
                        value={passKey}
                        onChange={(e) => {
                          setPassKey(e.target.value);
                          if (errorMessage) setErrorMessage("");
                        }}
                        placeholder="Enter pass key"
                        className="w-full rounded-2xl border-2 border-[#E4B94A] px-4 py-3 text-sm font-semibold text-[#2C272E] outline-none focus:border-[#562566]"
                      />
                    </div>
                  )}

                  <button
                    onClick={handleConfirmJoin}
                    disabled={joining}
                    className="relative flex w-full items-center rounded-full bg-[#562566] py-4 pl-4 pr-8 text-[18px] font-bold text-white disabled:opacity-70"
                  >
                    <span className="absolute left-2 flex h-[48px] w-[48px] items-center justify-center rounded-full bg-white text-[#562566]">
                      <ChevronRight size={30} />
                    </span>
                    <span className="flex-1 text-center">
                      {joining ? "Joining..." : "Join Contest"}
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <button className="flex w-full items-center rounded-full bg-[#F3B329] py-0 pl-5 pr-2 text-base font-bold text-white">
                    <span className="flex-1 text-center">
                      Successfully Joined
                    </span>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#F3B329]">
                      <ChevronRight size={22} />
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={handlePlayContest}
                    disabled={playing}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#562566] py-2 text-[18px] font-bold text-white disabled:opacity-70"
                  >
                    {playing ? "Checking..." : "Let's Play"}{" "}
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
            </div>

            {hasInsufficientBalance && joinStep === "confirm" && (
              <p className="mt-4 text-center text-[12px] font-medium text-[#B5424B]">
                Insufficient wallet balance. Recharge to continue.
              </p>
            )}

            <p className="mt-5 text-center text-[12px] leading-6 text-[#25212A]">
              This is a skill-based contest and does not involve any form of
              gambling or batting. By joining, you agree to Contest Rules and
              Terms.
            </p>
          </div>
        </div>
      )}

      {showReminderPopup && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/35 px-5 backdrop-blur-md"
          onClick={() => setShowReminderPopup(false)}
        >
          <div
            className="relative w-full max-w-[600px] overflow-hidden rounded-[30px] border-4 border-white/70 bg-white px-4 pb-4 pt-8 text-[#2E2933] shadow-[0_20px_60px_rgba(37,21,47,0.35)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowReminderPopup(false)}
              className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white text-[#1f1b24] shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
            >
              <X size={22} />
            </button>

            <div className="mx-auto mb-5 w-fit rounded-[18px] bg-[#562566] px-5 py-3 text-white shadow-sm">
              <div className="flex items-center justify-center gap-2 text-[16px] font-semibold leading-none">
                <Trophy size={19} className="text-[#F2A51A]" />
                <span>Total Rewards</span>
                <span className="font-bold text-[#FFD052]">
                  {contest.prizePoolCoin} Credits
                </span>
              </div>
            </div>

            <h2 className="mb-6 text-center text-[26px] font-bold leading-tight text-[#18151C]">
              {contest.contestName}
            </h2>

            <div className="rounded-[22px] bg-[#F4ECFF] px-5 py-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-left text-[20px] font-bold leading-tight text-[#18151C]">
                    Contest Joined
                  </h3>
                  <p className="mt-2 text-left text-[15px] font-semibold text-[#7A35A8]">
                    Upcoming Contest
                  </p>
                  <p className="mt-5 text-left text-[12px] leading-4 text-[#5F5968]">
                    This contest has not started yet. Check the timing below
                    and come back when it goes live.
                  </p>
                </div>

                <div className="flex h-[74px] w-[74px] shrink-0 items-center justify-center rounded-[22px] bg-white text-[#7A35A8] shadow-sm">
                  <CalendarDays size={34} />
                </div>
              </div>
            </div>

            <div className="relative z-10 -mt-1 rounded-[18px] bg-white px-6 py-5 shadow-[0_12px_35px_rgba(40,22,56,0.08)]">
              <div className="space-y-2 text-[15px]">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-semibold text-[#7A35A8]">Start</span>
                  <span className="text-right font-semibold text-[#1F1B24]">
                    {reminderStartLabel}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="font-semibold text-[#A77A00]">End</span>
                  <span className="text-right font-semibold text-[#1F1B24]">
                    {reminderEndLabel}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="font-semibold text-[#A77A00]">
                    Duration
                  </span>
                  <span className="text-right font-semibold text-[#1F1B24]">
                    {durationDisplay}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-[18px] bg-[#FFF7E8] px-2 py-2 text-left text-[12px] leading-7 text-[#4F4859]">
              This contest window runs from {reminderStartLabel} to{" "}
              {reminderEndLabel}. Keep {durationDisplay} free to complete it.
            </div>

            <button
              type="button"
              onClick={() => {
                setShowReminderPopup(false);
                navigate(getContestReturnUrl(), {
                  state: { contestId: contest._id },
                });
              }}
              className="mt-6 flex w-full items-center rounded-full bg-[#562566] py-4 pl-6 pr-5 text-[20px] font-bold text-white"
            >
              <span className="flex-1 text-center">Reminder Me</span>
              <ChevronRight size={30} />
            </button>

            <p className="mt-5 text-center text-[13px] leading-6 text-[#25212A]">
              This is a skill-based contest and does not involve any form of
              gambling or batting. By joining, you agree to Contest Rules and
              Terms.
            </p>
          </div>
        </div>
      )}

      {showFairPlayPopup && (
        <div
          className="fixed inset-0 z-[115] flex items-center justify-center bg-black/35 px-5 backdrop-blur-md"
          onClick={() => !playing && setShowFairPlayPopup(false)}
        >
          <div
            className="flex max-h-[82vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[30px] bg-white text-[#201B27] shadow-[0_20px_60px_rgba(37,21,47,0.35)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 px-5 pb-3 pt-5">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#F4ECFF] text-[#7A35A8]">
                <Shield size={38} />
              </div>

              <div className="min-w-0 flex-1 pt-1">
                <h2 className="text-left text-[26px] font-semibold leading-tight tracking-normal text-[#18151C]">
                  FAIR PLAY NOTICE
                </h2>
                <p className="mt-2 text-left text-[17px] font-medium text-[#6B2B86]">
                  Play Fair. Learn Fair. Win Fair.
                </p>
              </div>

              <button
                type="button"
                onClick={() => !playing && setShowFairPlayPopup(false)}
                disabled={playing}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#F5F2F7] text-[#1f1b24] disabled:opacity-60"
              >
                <X size={25} />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
              <div className="flex items-center gap-4 rounded-[18px] border border-[#E5DDF3] bg-[#F7F1FF] px-5 py-4 text-left shadow-sm">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-[#7A35A8]">
                  <Medal size={30} />
                </div>
                <p className="text-[16px] font-medium leading-6 text-[#3D3646]">
                  Adhyayi evaluates your own knowledge in quizzes and contests.
                </p>
              </div>

              <h3 className="mt-6 text-left text-[18px] font-bold text-[#3B174A]">
                Please avoid these shortcuts:
              </h3>

              <div className="mt-4 grid grid-cols-2 gap-3">
                {[
                  {
                    title: "AI tools",
                    text: "ChatGPT, Gemini and similar helpers",
                    icon: <Bot size={29} />,
                  },
                  {
                    title: "Lens / OCR",
                    text: "Camera-based answer lookup apps",
                    icon: <Camera size={29} />,
                  },
                  {
                    title: "Screen sharing",
                    text: "Live sharing or remote assistance",
                    icon: <MonitorUp size={29} />,
                  },
                  {
                    title: "Outside help",
                    text: "Any help from another person",
                    icon: <UsersRound size={29} />,
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="min-h-[150px] rounded-[16px] border border-[#E7E2EA] bg-white px-3 py-4 text-center shadow-sm"
                  >
                    <div className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#F4ECFF] text-[#7A35A8]">
                      {item.icon}
                      <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#F04444] text-white">
                        <X size={14} />
                      </span>
                    </div>
                    <p className="mt-4 text-[15px] font-bold text-[#171321]">
                      {item.title}
                    </p>
                    <p className="mt-3 text-[12px] leading-5 text-[#5F5968]">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>

           {/* Warning */}
  <div className="mt-5 rounded-2xl border border-[#F3D98B] bg-[#FFF8E9] p-4">

    <div className="flex items-center gap-2">
      <FiAlertTriangle className="text-[#C48B00]" size={20} />
      <span className="font-semibold text-[#5F4C19]">
        Suspicious behavior may lead to:
      </span>
    </div>

    <div className="mt-4 flex flex-wrap gap-2">
      {["Warning", "Reward review", "Verification", "Restrictions"].map(
        (item) => (
          <span
            key={item}
            className="rounded-full border border-[#E6D3A0] bg-white px-4 py-2 text-xs font-medium text-[#6A5A2A]"
          >
            {item}
          </span>
        )
      )}
    </div>
  </div>

  {/* Remember */}
  <div className="mt-4 rounded-2xl border border-[#D7F0DB] bg-[#F1FCF4] p-4">

    <div className="flex items-center gap-2">
      <FiTarget className="text-green-600" size={20} />
      <span className="font-semibold text-green-700">
        Remember
      </span>
    </div>

    <p className="mt-2 text-sm text-gray-600 text-">
      Learning comes first. Let knowledge win, not shortcuts.
    </p>
  </div>

  {/* Footer */}
  <p className="mt-5 text-center text-xs text-gray-500">
    By continuing, you agree to the Fair Play Policy.
  </p>
            </div>

            <div className="grid grid-cols-2 gap-3 border-t border-[#EEE8F2] bg-white px-5 py-4">
              <button
                type="button"
                onClick={() => setShowFairPlayPopup(false)}
                disabled={playing}
                className="rounded-[18px] border-2 border-[#6B2B86] bg-white py-4 text-[18px] font-bold text-[#6B2B86] disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={proceedToContestQuestion}
                disabled={playing}
                className="flex items-center justify-center gap-3 rounded-[18px] bg-gradient-to-r from-[#562566] to-[#8733C4] py-4 text-[18px] font-bold text-white disabled:opacity-70"
              >
                <Play size={21} fill="currentColor" />
                {playing ? "Checking..." : "Agree & Play"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCelebration && (
        <div className="pointer-events-none fixed inset-0 z-[120] overflow-hidden">
          {Array.from({ length: 18 }).map((_, index) => (
            <span
              key={index}
              className="absolute top-[-12px] h-3 w-2 animate-[contest-confetti_0.8s_ease-out_forwards] rounded-sm"
              style={{
                left: `${8 + index * 5}%`,
                animationDelay: `${(index % 6) * 0.04}s`,
                backgroundColor: [
                  "#F2A51A",
                  "#562566",
                  "#22C55E",
                  "#EF4444",
                  "#3B82F6",
                ][index % 5],
              }}
            />
          ))}
          <style>
            {`
              @keyframes contest-confetti {
                0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                100% { transform: translateY(65vh) rotate(420deg); opacity: 0; }
              }
            `}
          </style>
        </div>
      )}

      {showInfo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3 backdrop-blur-sm"
          onClick={() => setShowInfo(false)}
        >
          <div
            className="
    relative
    max-h-[78vh]
    w-full
    max-w-[520px]
    overflow-y-auto
    rounded-[28px]
    bg-[#F5F0FA]
    p-4
    sm:p-5
    shadow-2xl
  "
            onClick={(e) => e.stopPropagation()}
          >
            {/* CLOSE BUTTON */}
            <button
              onClick={() => setShowInfo(false)}
              className="
          absolute
          right-3
          top-3
          flex
          h-9
          w-9
          sm:h-10
          sm:w-10
          items-center
          justify-center
          rounded-full
          bg-white/60
          shadow
          text-black
        "
            >
              <X size={18} />
            </button>

            {/* CONTENT */}
            <div className="space-y-2 pt-4 text-[#50485A] text-left">
              <div className="flex items-start gap-3">
                <LinkIcon className=" shrink-0 text-[#E0A72F]" />
                <p className="text-sm sm:text-lg leading-6 sm:leading-8">
                  This is a skill-based contest. Rewards are based on
                  performance and are not guaranteed.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <Calculator className=" shrink-0 text-[#E0A72F]" />
                <p className="text-sm sm:text-lg leading-6 sm:leading-8">
                  Score = Right - (Wrong x 0.25)
                </p>
              </div>

              <div className="flex items-start gap-3">
                <BarChart3 className=" shrink-0 text-[#E0A72F]" />
                <p className="text-sm sm:text-lg leading-6 sm:leading-8">
                  Rankings are calculated automatically by the system.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <Shield className=" shrink-0 text-[#E0A72F]" />
                <p className="text-sm sm:text-lg leading-2 sm:leading-8">
                  Only one attempt allowed.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <AlarmClock className=" shrink-0 text-[#E0A72F]" />
                <p className="text-sm sm:text-lg leading-2 sm:leading-8">
                  Auto submit when timer ends.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <Edit3 className=" shrink-0 text-[#E0A72F]" />
                <p className="text-sm sm:text-lg leading-2 sm:leading-8">
                  Rankings cannot be manually modified.
                </p>
              </div>

              {/* RULES BOX */}
              <div className="rounded-3xl bg-[#F2BE55] p-2 sm:p-2 text-[#4B2D53]">
                {[
                  "Highest Score",
                  "Higher Accuracy",
                  "Less Time Taken",
                  "Earlier Submission",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-2 py-2 lg:py-0"
                  >
                    <Check className="h-4 w-4 sm:h-4 sm:w-5 shrink-0" />

                    <span className="text-sm sm:text-base">{item}</span>
                  </div>
                ))}
              </div>

              {/* FOOTER BOX */}
              <div
                className="
    rounded-3xl
    bg-[#F2BE55]
    p-4
    text-sm
    md:text-[15px]
    leading-6
    md:leading-7
    text-[#4B2D53]
  "
              >
                This platform does not involve any form of gambling or
                chance-based outcomes.
              </div>
            </div>
          </div>
        </div>
      )}
      {showPopup && popupType === "premium" && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 backdrop-blur-md p-4"
          onClick={() => setShowPopup(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="
        w-full
        max-w-[340px]
        lg:max-h-[540px]
        lg:max-w-[640px]
        sm:max-w-[380px]
        md:max-w-[450px]
        rounded-[28px]
        border-2
        border-[#F5A623]
        bg-[#4A2A6F]
        shadow-[0_20px_50px_rgba(0,0,0,0.35)]
        overflow-hidden
      "
          >
            <div className="p-6 sm:p-7 md:p-8">
              {/* Crown */}
              <div className="mb-6 flex justify-center">
                <div className="flex h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28 lg:h-20 lg:w-20 items-center justify-center rounded-full bg-[#F5B52E] shadow-[0_0_30px_rgba(245,181,46,0.4)]">
                  👑
                </div>
              </div>

              {/* Heading */}
              <h2 className="mb-4 text-center text-3xl sm:text-4xl md:text-5xl lg:text-[24px] font-bold leading-tight text-white">
                Premium Access
                <br />
                Required
              </h2>

              {/* Description */}
              <p className="mx-auto lg:mb-2 mb-6 max-w-[280px] text-center text-sm sm:text-base md:text-lg lg:text-[16px] leading-relaxed text-white/80">
                This contest is available for premium members only
              </p>

              {/* Benefits */}
              <div className="lg:mb-2 mb-8 space-y-3 text-white">
                <div className="flex items-center gap-4">
                  <Gift className="h-6 w-6 shrink-0 text-[#F5B52E]" />
                  <span className="text-base sm:text-lg">
                    5x Higher Reward Pool
                  </span>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-[#F5B52E]" />
                  <span className="text-base sm:text-lg leading-relaxed">
                    Access to Exclusive High-Value Contest
                  </span>
                </div>

                <div className="flex items-center gap-4">
                  <BarChart3 className="h-6 w-6 shrink-0 text-[#F5B52E]" />
                  <span className="text-base sm:text-lg">
                    Better Winning Opportunities
                  </span>
                </div>
              </div>

              {/* Highlight */}
              <p className="lg:mb-2 mb-8 text-center text-lg sm:text-xl font-bold leading-relaxed text-[#F5B52E]">
                Unlock premium once – access all premium contests
              </p>

              {/* Button */}
              <Link to="/dash/plan">
                <button
                  onClick={() => setShowPopup(false)}
                  className="
              mb-5
              w-full
              rounded-full
              border-2
              border-[#F5A623]
              bg-gradient-to-r
              from-[#9D4EDD]
              to-[#432371]
              py-3
              sm:py-4
              text-lg
              sm:text-xl
              font-bold
              text-white
              transition-all
              duration-300
              hover:scale-[1.02]
            "
                >
                  Unlock Premium
                </button>
              </Link>

              {/* Maybe Later */}
              <button
                onClick={() => setShowPopup(false)}
                className="block w-full text-center text-sm sm:text-base font-medium tracking-[0.15em] text-white/80"
              >
                MAYBE LATER
              </button>
            </div>
          </div>
        </div>
      )}

      <ContestResultModal
        isOpen={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        result={summaryResult}
        contestData={summaryResult}
        onGoDashboard={() => setShowSummaryModal(false)}
      />
    </>
  );
};

export default ContestCard;
